import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import dayjs from "dayjs"

import { updateProductor, sincronizarAfip, getEntidades } from "api/agro"
import toastHandler from "toastHandler"
import { formatearCuit } from "helpers/formatters"
import userStore from "userStore"

const UpdateSchema = Yup.object().shape({
  razonSocial: Yup.string().required("Requerido"),
  domicilioFiscal: Yup.string(),
  telefono: Yup.string(),
  email: Yup.string().email("Email inválido"),
})

const DatosGenerales = ({ productor, onUpdate }) => {
  const [modalEditar, setModalEditar] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const [entidades, setEntidades] = useState([])

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)
  const modificacion = userStore(state => state.modificacion)

  useEffect(() => {
    getEntidades()
      .then(data => setEntidades(data))
      .catch(err => console.error("Error cargando entidades:", err))
  }, [])

  if (!productor) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  // Sincronizar con ARCA (padron constancia de inscripcion)
  const handleSincronizarAfip = async () => {
    setSincronizando(true)
    try {
      await sincronizarAfip(productor.id)
      setSuccess("Datos actualizados desde ARCA")
      onUpdate && onUpdate()
    } catch (error) {
      setError("Error al sincronizar: " + error.message)
    } finally {
      setSincronizando(false)
    }
  }

  // Guardar cambios
  const handleGuardar = async (values, { setSubmitting }) => {
    try {
      await updateProductor(productor.id, {
        razonSocial: values.razonSocial,
        domicilioFiscal: values.domicilioFiscal,
        telefono: values.telefono,
        email: values.email,
        idEntidad: values.idEntidad ? parseInt(values.idEntidad, 10) : null,
        oficialComercial: values.oficialComercial,
      })
      setSuccess("Datos actualizados correctamente")
      setModalEditar(false)
      onUpdate && onUpdate()
    } catch (error) {
      setError("Error al guardar: " + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = date => {
    if (!date) return "-"
    return dayjs(date).format("DD/MM/YYYY HH:mm")
  }

  return (
    <>
      <Row>
        {/* Información Principal */}
        {/* <Col lg={8}> */}
        <Col>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <h5 className="card-title mb-0">Información del Productor</h5>
                {modificacion && (
                  <div>
                    <Button
                      color="info"
                      size="sm"
                      className="me-2"
                      onClick={handleSincronizarAfip}
                      disabled={sincronizando}
                    >
                      {sincronizando ? (
                        <Spinner size="sm" />
                      ) : (
                        <i className="mdi mdi-refresh me-1" />
                      )}
                      Sincronizar ARCA
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setModalEditar(true)}
                    >
                      <i className="mdi mdi-pencil me-1" />
                      Editar
                    </Button>
                  </div>
                )}
              </div>

              <Row>
                <Col md={6}>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="text-muted" width="40%">
                          CUIT
                        </td>
                        <td>
                          <strong>{formatearCuit(productor.cuit)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Razón Social</td>
                        <td>{productor.razonSocial}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Tipo Persona</td>
                        <td>
                          <Badge
                            color={
                              productor.tipoPersona === "JURIDICA"
                                ? "info"
                                : "secondary"
                            }
                          >
                            {productor.tipoPersona}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Estado CUIT</td>
                        <td>
                          <Badge
                            color={
                              productor.estadoCuit === "ACTIVO"
                                ? "success"
                                : "danger"
                            }
                          >
                            {productor.estadoCuit}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">ID Cliente</td>
                        <td>{productor.idClienteLegacy || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="text-muted" width="40%">
                          Domicilio Fiscal
                        </td>
                        <td>{productor.domicilioFiscal || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Teléfono</td>
                        <td>{productor.telefono || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Email</td>
                        <td>{productor.email || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Actividad Principal</td>
                        <td>
                          {productor.actividadPrincipal ? (
                            <>
                              {productor.actividadPrincipalCodigo && (
                                <Badge color="primary" className="me-2">
                                  {productor.actividadPrincipalCodigo}
                                </Badge>
                              )}
                              {productor.actividadPrincipal}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Condición IVA</td>
                        <td>
                          {productor.condicionIva || "-"}
                          {productor.esMonotributista &&
                            productor.categoriaMonotributo && (
                              <Badge
                                color="light"
                                className="text-dark border ms-2"
                              >
                                Cat. {productor.categoriaMonotributo}
                              </Badge>
                            )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Localidad / Provincia</td>
                        <td>
                          {[productor.localidad, productor.provincia]
                            .filter(Boolean)
                            .join(", ") || "-"}
                          {productor.codigoPostal
                            ? ` (CP ${productor.codigoPostal})`
                            : ""}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Entidad principal</td>
                        <td>{productor.entidad || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Oficial Comercial</td>
                        <td>{productor.oficialComercial || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>

              <hr />

              <Row>
                <Col md={4}>
                  <div className="text-center">
                    <h4 className="mb-1">
                      {productor.superficieTotalHa?.toLocaleString("es-AR") ||
                        0}
                    </h4>
                    <small className="text-muted">Hectáreas Totales</small>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center">
                    <span className="text-muted small">
                      Última Actualización ARCA
                    </span>
                    <div>
                      {formatDate(productor.fechaUltimaActualizacionAfip)}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center">
                    <span className="text-muted small">Fecha de Alta</span>
                    <div>{formatDate(productor.fechaCreacion)}</div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* Panel Lateral */}
        {/* <Col lg={4}> */}
        {/* Vinculación EWAN */}
        {/* <Card className="mb-3">
            <CardBody>
              <h6 className="card-title">Vinculación EWAN</h6>
              {productor.idClienteEwan ? (
                <div>
                  <Badge color="success" className="mb-2">Vinculado</Badge>
                  <p className="mb-0 small text-muted">
                    ID Cliente: {productor.idClienteEwan}
                  </p>
                </div>
              ) : (
                <div>
                  <Badge color="warning" className="mb-2">Sin vincular</Badge>
                  <p className="mb-0 small text-muted">
                    Este productor no está vinculado a un cliente en EWAN
                  </p>
                  <Button color="primary" size="sm" className="mt-2" outline>
                    Vincular Cliente
                  </Button>
                </div>
              )}
            </CardBody>
          </Card> */}

        {/* Resumen Rápido */}
        {/* <Card>
            <CardBody>
              <h6 className="card-title">Acciones Rápidas</h6>
              <div className="d-grid gap-2">
                <Button color="success" outline size="sm">
                  <i className="mdi mdi-file-pdf-box me-1" />
                  Generar Informe PDF
                </Button>
                <Button color="info" outline size="sm">
                  <i className="mdi mdi-history me-1" />
                  Ver Historial
                </Button>
                <Button color="warning" outline size="sm">
                  <i className="mdi mdi-file-document me-1" />
                  Documentación
                </Button>
              </div>
            </CardBody>
          </Card> */}
        {/* </Col> */}
      </Row>

      {/* Modal Editar */}
      <Modal
        isOpen={modalEditar}
        toggle={() => setModalEditar(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setModalEditar(false)}>
          Editar Datos del Productor
        </ModalHeader>
        <Formik
          initialValues={{
            razonSocial: productor.razonSocial || "",
            domicilioFiscal: productor.domicilioFiscal || "",
            telefono: productor.telefono || "",
            email: productor.email || "",
            idEntidad: productor.idEntidad || "",
            oficialComercial: productor.oficialComercial || "",
          }}
          validationSchema={UpdateSchema}
          onSubmit={handleGuardar}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <ModalBody>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Razón Social *</Label>
                      <Field
                        name="razonSocial"
                        as={Input}
                        invalid={touched.razonSocial && !!errors.razonSocial}
                      />
                      {touched.razonSocial && errors.razonSocial && (
                        <div className="text-danger small">
                          {errors.razonSocial}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Domicilio Fiscal</Label>
                      <Field name="domicilioFiscal" as={Input} />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Teléfono</Label>
                      <Field name="telefono" as={Input} />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Email</Label>
                      <Field
                        name="email"
                        as={Input}
                        type="email"
                        invalid={touched.email && !!errors.email}
                      />
                      {touched.email && errors.email && (
                        <div className="text-danger small">{errors.email}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Entidad principal</Label>
                      <Field name="idEntidad" as={Input} type="select">
                        <option value="">-- Seleccione --</option>
                        {entidades.map(entidad => (
                          <option key={entidad.id} value={entidad.id}>
                            {entidad.descripcion}
                          </option>
                        ))}
                      </Field>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Oficial Comercial</Label>
                      <Field name="oficialComercial" as={Input} />
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => setModalEditar(false)}>
                  Cancelar
                </Button>
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  )
}

export default DatosGenerales
